import { useCallback, useEffect, useState } from "react";
import AppTheme from "../../../theme/AppTheme";
import { dataGridCustomizations } from "../../../theme/customizations";
import {
  Alert,
  Box,
  Button,
  Chip,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  notifyError,
  notifySuccess,
} from "../../../components/ToastContainer/Toast/Toast";
import {
  BonusTierRow,
  deleteBonusTier,
  getBonusTiers,
  getPromoCodes,
  getPromotionReport,
  getPromotionSettings,
  PromoCodeRow,
  PromotionReport,
  PromotionSettings,
  saveBonusTier,
  savePromoCode,
  savePromotionSettings,
} from "../../../service/BuyerConfiguration/Promotions/promotionService";

/**
 * Wallet promotions.
 *
 * Cashback, referrals, promo codes and top-up bonuses - every number here is
 * ours to set and every feature starts off. Two rules the Buyer API enforces,
 * worth knowing while editing this page:
 *
 *  - A reward already promised is frozen. Turning referrals off does not take
 *    back a referral someone has already joined, and a top-up already started
 *    still pays the bonus it was offered.
 *  - Cashback and referral rewards are not paid at delivery. They wait for
 *    the hold, so an order refunded inside that window pays nothing.
 */

const inr = (n: number | null | undefined) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const Promotions = (props: { disableCustomTheme?: boolean }) => {
  const xThemeComponents = { ...dataGridCustomizations };

  const [settings, setSettings] = useState<Record<string, any> | null>(null);
  const [report, setReport] = useState<PromotionReport | null>(null);
  const [codes, setCodes] = useState<PromoCodeRow[]>([]);
  const [tiers, setTiers] = useState<BonusTierRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [codeForm, setCodeForm] = useState<Record<string, any> | null>(null);
  const [tierForm, setTierForm] = useState<Record<string, any> | null>(null);

  const fail = (e: any, fallback: string) =>
    notifyError(e?.response?.data?.message || e?.message || fallback);

  const loadAll = useCallback(() => {
    getPromotionSettings()
      .then((s) => setSettings({ ...s }))
      .catch((e) => fail(e, "Failed to load promotion settings"));
    getPromotionReport().then(setReport).catch(() => undefined);
    getPromoCodes().then(setCodes).catch(() => undefined);
    getBonusTiers().then(setTiers).catch(() => undefined);
  }, []);

  useEffect(loadAll, [loadAll]);

  const set = (key: string, value: any) =>
    setSettings((prev) => (prev ? { ...prev, [key]: value } : prev));

  const num = (v: any) => Number(v || 0);

  const problem = (() => {
    if (!settings) return null;
    if (settings.cashback_enabled) {
      if (!(num(settings.cashback_percent) > 0 && num(settings.cashback_percent) <= 50))
        return "Cashback percent must be between 0 and 50.";
      if (num(settings.cashback_max_amount) <= 0)
        return "Set a cashback cap per order, or customers on large orders get an unbounded reward.";
      if (num(settings.cashback_hold_hours) < 1)
        return "A hold of at least 1 hour is needed, otherwise a refunded order still pays cashback.";
    }
    if (settings.referral_enabled) {
      if (num(settings.referral_referrer_amount) <= 0 && num(settings.referral_referee_amount) <= 0)
        return "Set at least one referral reward.";
    }
    return null;
  })();

  const saveSettings = async () => {
    if (!settings || problem) return;
    setSaving(true);
    try {
      const saved = await savePromotionSettings({
        cashback_enabled: !!settings.cashback_enabled,
        cashback_percent: num(settings.cashback_percent),
        cashback_max_amount: num(settings.cashback_max_amount),
        cashback_min_order: num(settings.cashback_min_order),
        cashback_hold_hours: num(settings.cashback_hold_hours),
        cashback_expiry_days: num(settings.cashback_expiry_days),
        cashback_orders: settings.cashback_orders === "online" ? "online" : "all",
        referral_enabled: !!settings.referral_enabled,
        referral_referrer_amount: num(settings.referral_referrer_amount),
        referral_referee_amount: num(settings.referral_referee_amount),
        referral_min_order: num(settings.referral_min_order),
        referral_expiry_days: num(settings.referral_expiry_days),
        promo_codes_enabled: !!settings.promo_codes_enabled,
        promo_default_expiry_days: num(settings.promo_default_expiry_days),
        promo_expiry_reminder_days: num(settings.promo_expiry_reminder_days),
      } as PromotionSettings);
      setSettings({ ...saved });
      notifySuccess("Promotion settings saved");
      getPromotionReport().then(setReport).catch(() => undefined);
    } catch (e) {
      fail(e, "Failed to save promotion settings");
    } finally {
      setSaving(false);
    }
  };

  const submitCode = async () => {
    if (!codeForm) return;
    setSaving(true);
    try {
      setCodes(
        await savePromoCode({
          id: codeForm.id,
          code: String(codeForm.code || "").trim().toUpperCase(),
          amount: num(codeForm.amount),
          expires_in_days: num(codeForm.expires_in_days),
          min_order_value: num(codeForm.min_order_value),
          max_redemptions: codeForm.max_redemptions ? num(codeForm.max_redemptions) : null,
          per_user_limit: num(codeForm.per_user_limit) || 1,
          is_active: !!codeForm.is_active,
        }),
      );
      notifySuccess("Promo code saved");
      setCodeForm(null);
    } catch (e) {
      fail(e, "Failed to save the promo code");
    } finally {
      setSaving(false);
    }
  };

  const submitTier = async () => {
    if (!tierForm) return;
    setSaving(true);
    try {
      setTiers(
        await saveBonusTier({
          id: tierForm.id,
          min_amount: num(tierForm.min_amount),
          bonus_amount: num(tierForm.bonus_amount),
          expires_in_days: num(tierForm.expires_in_days),
          min_order_value: num(tierForm.min_order_value),
          is_active: !!tierForm.is_active,
        }),
      );
      notifySuccess("Bonus tier saved");
      setTierForm(null);
    } catch (e) {
      fail(e, "Failed to save the bonus tier");
    } finally {
      setSaving(false);
    }
  };

  const removeTier = async (id: number) => {
    try {
      setTiers(await deleteBonusTier(id));
      notifySuccess("Bonus tier removed");
    } catch (e) {
      fail(e, "Failed to remove the bonus tier");
    }
  };

  const codeColumns: GridColDef[] = [
    { field: "code", headerName: "Code", flex: 1, minWidth: 130 },
    {
      field: "amount",
      headerName: "Credit",
      width: 110,
      renderCell: (p) => inr(p.row.amount),
    },
    {
      field: "expires_in_days",
      headerName: "Valid",
      width: 100,
      renderCell: (p) => `${p.row.expires_in_days} days`,
    },
    {
      field: "min_order_value",
      headerName: "Min order",
      width: 110,
      renderCell: (p) => (Number(p.row.min_order_value) > 0 ? inr(p.row.min_order_value) : "-"),
    },
    {
      field: "redeemed",
      headerName: "Used",
      width: 110,
      renderCell: (p) =>
        p.row.max_redemptions
          ? `${p.row.redeemed} / ${p.row.max_redemptions}`
          : String(p.row.redeemed ?? 0),
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 110,
      renderCell: (p) => (
        <Chip
          size="small"
          label={p.row.is_active ? "Live" : "Off"}
          color={p.row.is_active ? "success" : "default"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 90,
      sortable: false,
      renderCell: (p) => (
        <Button size="small" onClick={() => setCodeForm({ ...p.row })}>
          Edit
        </Button>
      ),
    },
  ];

  const tierColumns: GridColDef[] = [
    {
      field: "min_amount",
      headerName: "Top up at least",
      flex: 1,
      minWidth: 150,
      renderCell: (p) => inr(p.row.min_amount),
    },
    {
      field: "bonus_amount",
      headerName: "Bonus credit",
      width: 140,
      renderCell: (p) => inr(p.row.bonus_amount),
    },
    {
      field: "expires_in_days",
      headerName: "Valid",
      width: 110,
      renderCell: (p) => `${p.row.expires_in_days} days`,
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 110,
      renderCell: (p) => (
        <Chip
          size="small"
          label={p.row.is_active ? "Live" : "Off"}
          color={p.row.is_active ? "success" : "default"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 160,
      sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => setTierForm({ ...p.row })}>
            Edit
          </Button>
          <Button size="small" color="error" onClick={() => removeTier(p.row.id)}>
            Remove
          </Button>
        </Stack>
      ),
    },
  ];

  const numberField = (label: string, key: string, adornment?: string, help?: string) => (
    <TextField
      label={label}
      size="small"
      type="number"
      value={settings?.[key] ?? 0}
      onChange={(e) => set(key, e.target.value)}
      helperText={help}
      InputProps={
        adornment
          ? adornment === "₹"
            ? { startAdornment: <InputAdornment position="start">₹</InputAdornment> }
            : { endAdornment: <InputAdornment position="end">{adornment}</InputAdornment> }
          : undefined
      }
      sx={{ minWidth: 170 }}
    />
  );

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" }, p: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5">Wallet Promotions</Typography>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            Cashback, referrals, promo codes and top-up bonuses. Promotional credit can only be
            spent on Tazty and expires; what has already been promised to a customer is always
            honoured, even after a feature is switched off.
          </Typography>
        </Box>

        {report && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
              {[
                ["Promo credit given", inr(report.promo_given)],
                ["Cashback given", inr(report.cashback_given)],
                ["Referral rewards", inr(report.referral_given)],
                ["Expired unused", inr(report.expired)],
                ["Outstanding (unspent)", inr(report.outstanding_lots)],
                [
                  "Waiting to be paid",
                  `${inr(report.scheduled.amount)} (${report.scheduled.count})`,
                ],
              ].map(([label, value]) => (
                <Box key={label as string}>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    {label}
                  </Typography>
                  <Typography variant="h6">{value}</Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        )}

        {settings && (
          <>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="h6">Cashback</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!settings.cashback_enabled}
                      onChange={(e) => set("cashback_enabled", e.target.checked)}
                    />
                  }
                  label={settings.cashback_enabled ? "On" : "Off"}
                />
              </Stack>
              <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
                A share of the item value after discounts, credited after delivery plus the hold
                below. An order refunded during the hold pays nothing.
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                {numberField("Percent of order", "cashback_percent", "%")}
                {numberField("Cap per order", "cashback_max_amount", "₹")}
                {numberField("Minimum order", "cashback_min_order", "₹")}
                {numberField("Hold after delivery", "cashback_hold_hours", "hours")}
                {numberField("Credit valid for", "cashback_expiry_days", "days")}
                <TextField
                  select
                  size="small"
                  label="Which orders"
                  value={settings.cashback_orders || "all"}
                  onChange={(e) => set("cashback_orders", e.target.value)}
                  sx={{ minWidth: 190 }}
                >
                  <MenuItem value="all">All orders (COD and online)</MenuItem>
                  <MenuItem value="online">Prepaid orders only</MenuItem>
                </TextField>
              </Stack>
              {settings.cashback_enabled && num(settings.cashback_percent) > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  A ₹500 order (after discounts) earns{" "}
                  {inr(
                    Math.min(
                      (500 * num(settings.cashback_percent)) / 100,
                      num(settings.cashback_max_amount) || Infinity,
                    ),
                  )}
                  , credited {num(settings.cashback_hold_hours)} hours after delivery and valid for{" "}
                  {num(settings.cashback_expiry_days)} days.
                </Alert>
              )}
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="h6">Referrals</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!settings.referral_enabled}
                      onChange={(e) => set("referral_enabled", e.target.checked)}
                    />
                  }
                  label={settings.referral_enabled ? "On" : "Off"}
                />
              </Stack>
              <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
                Both rewards are paid after the new customer's first delivered order. The amounts
                are fixed when they join, so anyone already referred still gets what they were
                promised if these numbers change or referrals are switched off.
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                {numberField("Reward to the referrer", "referral_referrer_amount", "₹")}
                {numberField("Reward to the new customer", "referral_referee_amount", "₹")}
                {numberField("Minimum first order", "referral_min_order", "₹")}
                {numberField("Credit valid for", "referral_expiry_days", "days")}
              </Stack>
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="h6">Promo codes</Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!settings.promo_codes_enabled}
                        onChange={(e) => set("promo_codes_enabled", e.target.checked)}
                      />
                    }
                    label={settings.promo_codes_enabled ? "On" : "Off"}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      setCodeForm({
                        code: "",
                        amount: 50,
                        expires_in_days: num(settings.promo_default_expiry_days) || 7,
                        min_order_value: 0,
                        per_user_limit: 1,
                        max_redemptions: "",
                        is_active: false,
                      })
                    }
                  >
                    New code
                  </Button>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                {numberField("Default validity", "promo_default_expiry_days", "days")}
                {numberField(
                  "Remind before expiry",
                  "promo_expiry_reminder_days",
                  "days",
                  "0 = no reminder",
                )}
              </Stack>
              <Box sx={{ height: 320 }}>
                <DataGrid
                  rows={codes}
                  columns={codeColumns}
                  getRowId={(r) => r.id}
                  density="compact"
                  disableRowSelectionOnClick
                  hideFooterSelectedRowCount
                />
              </Box>
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="h6">Top-up bonus</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    setTierForm({
                      min_amount: 2000,
                      bonus_amount: 100,
                      expires_in_days: 3,
                      min_order_value: 0,
                      is_active: true,
                    })
                  }
                >
                  New tier
                </Button>
              </Stack>
              <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
                "Add ₹2,000, get ₹100 extra." The customer sees the bonus before they pay and the
                largest tier they reach applies. The master switch lives on the Wallets page
                (TOPUP_BONUS_ENABLED); a top-up already started keeps the bonus it was offered.
              </Typography>
              <Box sx={{ height: 280 }}>
                <DataGrid
                  rows={tiers}
                  columns={tierColumns}
                  getRowId={(r) => r.id}
                  density="compact"
                  disableRowSelectionOnClick
                  hideFooterSelectedRowCount
                />
              </Box>
            </Paper>

            {problem && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {problem}
              </Alert>
            )}
            <Stack direction="row" spacing={2}>
              <Button variant="contained" onClick={saveSettings} disabled={saving || !!problem}>
                Save settings
              </Button>
              <Button onClick={loadAll} disabled={saving}>
                Reset
              </Button>
            </Stack>
          </>
        )}

        <Dialog open={!!codeForm} onClose={() => setCodeForm(null)} fullWidth maxWidth="sm">
          <DialogTitle>{codeForm?.id ? "Edit promo code" : "New promo code"}</DialogTitle>
          <Divider />
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Code"
                size="small"
                value={codeForm?.code ?? ""}
                onChange={(e) =>
                  setCodeForm((f) => ({ ...(f || {}), code: e.target.value.toUpperCase() }))
                }
                helperText="Letters, numbers, - and _ only. The customer types this in the app."
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Credit"
                  size="small"
                  type="number"
                  value={codeForm?.amount ?? 0}
                  onChange={(e) => setCodeForm((f) => ({ ...(f || {}), amount: e.target.value }))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
                <TextField
                  label="Valid for"
                  size="small"
                  type="number"
                  value={codeForm?.expires_in_days ?? 7}
                  onChange={(e) =>
                    setCodeForm((f) => ({ ...(f || {}), expires_in_days: e.target.value }))
                  }
                  InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Minimum order"
                  size="small"
                  type="number"
                  value={codeForm?.min_order_value ?? 0}
                  onChange={(e) =>
                    setCodeForm((f) => ({ ...(f || {}), min_order_value: e.target.value }))
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  helperText="0 = any order"
                />
                <TextField
                  label="Uses per customer"
                  size="small"
                  type="number"
                  value={codeForm?.per_user_limit ?? 1}
                  onChange={(e) =>
                    setCodeForm((f) => ({ ...(f || {}), per_user_limit: e.target.value }))
                  }
                />
                <TextField
                  label="Total uses"
                  size="small"
                  type="number"
                  value={codeForm?.max_redemptions ?? ""}
                  onChange={(e) =>
                    setCodeForm((f) => ({ ...(f || {}), max_redemptions: e.target.value }))
                  }
                  helperText="Blank = no limit"
                />
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!codeForm?.is_active}
                    onChange={(e) =>
                      setCodeForm((f) => ({ ...(f || {}), is_active: e.target.checked }))
                    }
                  />
                }
                label="Live - customers can redeem it now"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCodeForm(null)}>Cancel</Button>
            <Button variant="contained" onClick={submitCode} disabled={saving}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!tierForm} onClose={() => setTierForm(null)} fullWidth maxWidth="sm">
          <DialogTitle>{tierForm?.id ? "Edit bonus tier" : "New bonus tier"}</DialogTitle>
          <Divider />
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Top up at least"
                  size="small"
                  type="number"
                  value={tierForm?.min_amount ?? 0}
                  onChange={(e) =>
                    setTierForm((f) => ({ ...(f || {}), min_amount: e.target.value }))
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
                <TextField
                  label="Bonus credit"
                  size="small"
                  type="number"
                  value={tierForm?.bonus_amount ?? 0}
                  onChange={(e) =>
                    setTierForm((f) => ({ ...(f || {}), bonus_amount: e.target.value }))
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
                <TextField
                  label="Valid for"
                  size="small"
                  type="number"
                  value={tierForm?.expires_in_days ?? 3}
                  onChange={(e) =>
                    setTierForm((f) => ({ ...(f || {}), expires_in_days: e.target.value }))
                  }
                  InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                />
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!tierForm?.is_active}
                    onChange={(e) =>
                      setTierForm((f) => ({ ...(f || {}), is_active: e.target.checked }))
                    }
                  />
                }
                label="Live"
              />
              <Alert severity="info">
                The bonus must be smaller than the top-up. A customer adding{" "}
                {inr(tierForm?.min_amount)} gets {inr(tierForm?.bonus_amount)} promo credit to spend
                within {Number(tierForm?.expires_in_days || 0)} days.
              </Alert>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTierForm(null)}>Cancel</Button>
            <Button variant="contained" onClick={submitTier} disabled={saving}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AppTheme>
  );
};

export default Promotions;
